<template>
  <div class="h-full flex flex-col p-4">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <base-toast v-if="!!baseToast.message" :message="baseToast.message" :type="baseToast.type" @close="baseToast = { type: '', message: '' }"></base-toast>
    <section key="2" class="h-full flex flex-col">
      <base-card class="h-full flex flex-col">

        <!-- Header Card: Title + Refresh + Search + Quick Actions -->
        <div class="flex items-center gap-3 px-4 py-3 border-b flex-wrap">
          <!-- Title -->
          <div class="text-base font-semibold text-gray-700 flex-shrink-0">Pay-In List</div>

          <!-- Refresh button -->
          <button @click.stop="getPayInList" title="Refresh" class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <!-- Search bar -->
          <div class="flex-1 min-w-48 relative">
            <input
              v-model.trim="searchKeyWord"
              type="text"
              placeholder="Search..."
              class="w-full pl-3 pr-8 py-1.5 border border-gray-300 rounded-full text-sm focus:outline-none focus:border-blue-400"
            />
            <button v-if="searchKeyWord" @click="searchKeyWord = ''" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Quick action buttons -->
          <div class="flex items-center gap-1 flex-shrink-0 flex-wrap">
            <button @click.stop="toggleFilters" :class="showSearch ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'" class="px-3 py-1.5 rounded text-xs font-medium">
              Filters
            </button>
            <button v-if="role == 'user'" @click.stop="$router.push('/payin/verify-statement')" class="px-3 py-1.5 rounded text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50">
              Verify
            </button>
            <button v-if="role == 'subadmin'" @click.stop="showUploadModal = true" class="px-3 py-1.5 rounded text-xs font-medium border border-blue-500 text-blue-600 hover:bg-blue-50">
              Upload
            </button>
            <button @click.stop="exportData" class="px-3 py-1.5 rounded text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50">
              Export
            </button>
            <button
              v-for="s in quickStatuses" :key="s.value"
              @click.stop="paymentStatus = s.value"
              :class="paymentStatus === s.value ? s.activeClass : 'border border-gray-300 text-gray-600 hover:bg-gray-50'"
              class="px-3 py-1.5 rounded text-xs font-medium">
              {{ s.label }}
            </button>
          </div>
        </div>

        <!-- Advanced Filters Panel (collapsible) -->
        <div v-if="showSearch" class="flex flex-col mx-4 bg-gray-100 mt-3 pb-4 rounded-lg">
          <div :class="isAdmin ? 'grid-cols-4' : 'grid-cols-3'" class="w-full grid gap-4 mt-3 px-4">
            <div class="flex flex-col">
              <input v-model.trim="amount" type="number" placeholder="Amount" />
            </div>
            <div v-if="isAdmin" class="flex flex-col">
              <select v-model.trim="validatorUsername">
                <option value="">All Banks</option>
                <option v-for="(agent, index) in agents" :key="index" :value="agent.username">{{ agent.username }}</option>
              </select>
            </div>
            <div class="flex flex-col">
              <select v-model.trim="paymentStatus">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="unassigned">Unassigned</option>
                <option value="expired">Expired</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div class="flex flex-col">
              <select v-model.trim="utrFilter">
                <option value="">All Orders</option>
                <option value="with_utr">With UTR</option>
                <option value="without_utr">Without UTR</option>
              </select>
            </div>
          </div>
          <div class="w-full flex flex-row flex-wrap mt-3 px-4 gap-4">
            <div class="flex-1 min-w-32">
              <div class="text-gray-500 text-xs mb-1">Start Date</div>
              <input type="date" v-model.trim="startDate" />
            </div>
            <div class="flex-1 min-w-32">
              <div class="text-gray-500 text-xs mb-1">End Date</div>
              <input type="date" v-model.trim="endDate" />
            </div>
            <div class="flex items-end">
              <button @click.stop="resetFilters" class="px-3 py-1.5 text-xs border border-red-400 text-red-500 rounded hover:bg-red-50">Reset</button>
            </div>
          </div>
        </div>

        <base-spinner v-if="isLoading" class="flex-auto"></base-spinner>
        <div v-else-if="hasPayInList && !isLoading" class="flex-auto flex flex-col mt-4 overflow-y-auto px-4 mb-4">
          <div class="grid grid-cols-1 gap-4">
            <pay-in-item v-for="(item, index) in filteredPayInList" :key="index" :item="item"
              :sl-no="paginatorInfo.firstItem + index"></pay-in-item>
          </div>
          <div class="mt-4 flex-auto flex flex-col justify-end">
            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
          </div>
        </div>
        <h3 v-else class="text-center font-medium flex-auto flex flex-col justify-center items-center text-base sm:text-xl">
          No Pay In transactions found!
        </h3>
      </base-card>
    </section>

    <base-dialog :show="showUploadModal" title="Upload Bank Statement" @close="showUploadModal = false">
      <div class="p-4">
        <div class="mb-4">
          <label class="block mb-2">Select Bank</label>
          <select v-model="selectedBank" class="w-full border p-2 rounded">
            <option value="">-- Select Bank --</option>
            <option v-for="agent in agents" :key="agent.username" :value="agent.username">
              {{ agent.name }} ({{ agent.username }})
            </option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block mb-2">Upload Bank Statement File</label>
          <div
              @dragover.prevent="isDraggingPayin = true"
              @dragleave.prevent="isDraggingPayin = false"
              @drop.prevent="handleFileDropPayin"
              :class="['border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors', isDraggingPayin ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400']"
              @click="$refs.payinFileInput.click()"
          >
              <input ref="payinFileInput" type="file" @change="handleFile" accept=".csv" class="hidden">
              <p v-if="!uploadFile" class="text-gray-600">
                  <span class="font-semibold">Click to upload</span> or drag and drop<br>
              </p>
              <p v-else class="text-green-600 font-semibold">{{ uploadFile.name }}</p>
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button @click="uploadStatement" :disabled="!selectedBank || !uploadFile || isUploading"
                  class="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
            {{ isUploading ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>
    </base-dialog>
  </div>
</template>

<script>
import PayInItem from "../../components/payin/PayInItem.vue";
import http from "../../http-common";
import io from "socket.io-client";
import Pagination from "../../components/shared/pagination.vue";
import { playNotificationSound } from "../../utils/notificationSound";

export default {
  name: "pay-in",
  components: {
    PayInItem,
    Pagination,
  },
  data() {
    return {
      isLoading: false,
      error: null,
      baseToast: {
        type: "",
        message: "",
      },
      showUploadModal: false,
      selectedBank: "",
      uploadFile: null,
      isUploading: false,
      isDraggingPayin: false,
      channel: null,
      showSearch: false,
      searchKeyWord: "",
      amount: null,
      paymentStatus: "",
      validatorUsername: "",
      utrFilter: "",
      startDate: "",
      endDate: "",
      usersWithRole: [],
      socket: null,
      first: 10,
      page: 1,
      quickStatuses: [
        { value: "", label: "All", activeClass: "bg-gray-700 text-white" },
        { value: "pending", label: "Pending", activeClass: "bg-yellow-500 text-white" },
        { value: "approved", label: "Approved", activeClass: "bg-green-600 text-white" },
        { value: "unassigned", label: "Unassigned", activeClass: "bg-blue-500 text-white" },
        { value: "expired", label: "Expired", activeClass: "bg-red-500 text-white" },
        { value: "rejected", label: "Rejected", activeClass: "bg-gray-500 text-white" },
      ],
    };
  },
  watch: {
    page() {
      this.getPayInList();
    },
    showSearch(newVal) {
      if (newVal) {
        const currentDate = new Date().toISOString().split("T")[0];
        this.startDate = this.endDate = currentDate;
      }
      if (!newVal) {
        this.startDate = this.endDate = "";
        this.getPayInList();
      }
    },
    searchKeyWord() {
      this.page = 1;
      this.getPayInList();
    },
    amount() {
      this.page = 1;
      this.getPayInList();
    },
    paymentStatus() {
      this.page = 1;
      this.getPayInList();
    },
    validatorUsername() {
      this.page = 1;
      this.getPayInList();
    },
    startDate(newVal) {
      if (this.endDate || newVal == "") {
        this.page = 1;
        this.getPayInList();
      }
    },
    endDate(newVal) {
      if (this.startDate || newVal == "") {
        this.page = 1;
        this.getPayInList();
      }
    },
  },
  computed: {
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    paginatorInfo() {
      return this.$store.getters["payin/pagination"];
    },
    filteredPayInList() {
      let list = this.$store.getters["payin/payInList"];

      if (this.utrFilter === 'with_utr') {
        list = list.filter(item => item.customerUtr && item.customerUtr.trim() !== '');
      } else if (this.utrFilter === 'without_utr') {
        list = list.filter(item => !item.customerUtr || item.customerUtr.trim() === '');
      }

      return list;
    },
    hasPayInList() {
      return this.$store.getters["payin/hasPayInList"];
    },
    role() {
      return this.$store.getters.role;
    },
    username() {
      return this.$store.getters.username;
    },
    agents() {
      return this.usersWithRole;
    },
    vendor() {
      return this.$store.getters.vendor;
    },
  },
  created() {
    this.socket = io(process.env.VUE_APP_SOCKET_URL, {
      path: "/jiopayu-socket-path",
    });

    this.socket.on("connect", () => {
      console.log("Connected to server");
    });

    this.socket.on("disconnect", () => {
      console.log("Disconnected from server");
    });

    this.getPayInList();
    this.getUsersWithRole();

    if (this.role == "admin" || this.role == "subadmin") {
      this.socket.on(`${this.vendor}-order-created`, (data) => {
        this.addOrderToPayInList(data);
        this.playSound('payin');
      });
      this.socket.on(`${this.vendor}-order-amount-created`, (data) => {
        this.updateAmountAndStatus(data);
      });
    } else {
      this.socket.on(`${this.vendor}-order-created-${this.username}`, (data) => {
        this.addOrderToPayInList(data);
        this.playSound('payin');
      });
      this.socket.on(`${this.vendor}-order-amount-created-${this.username}`, (data) => {
        this.updateAmountAndStatus(data);
      });
    }
    this.socket.on(`${this.vendor}-customer-upi-updated`, (data) => {
      this.updateCustomerUpiID(data);
    });
    this.socket.on(`${this.vendor}-customer-utr-updated`, (data) => {
      this.updateCustomerUtr(data);
    });
    this.socket.on(`${this.vendor}-customer-screenshot-updated`, (data) => {
      this.updateCustomerScreenshot(data);
    });
    this.socket.on(`${this.vendor}-order-update-status-and-trnx`, (data) => {
      this.updateStatusAndTransaction(data);
    });
    this.socket.on("order-approved-auto", (data) => {
      this.autoOrderApproved(data);
    });
  },
  methods: {
    playSound(type) {
      playNotificationSound(type);
    },
    toggleFilters() {
      this.showSearch = !this.showSearch;
    },
    resetFilters() {
      this.amount = null;
      this.paymentStatus = "";
      this.validatorUsername = "";
      this.utrFilter = "";
      this.startDate = "";
      this.endDate = "";
      this.page = 1;
      this.getPayInList();
    },
    handleFile(e) {
      this.uploadFile = e.target.files[0];
    },
    handleFileDropPayin(event) {
      this.isDraggingPayin = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadFile = files[0];
      }
    },
    async uploadStatement() {
      if (!this.selectedBank || !this.uploadFile) return;

      this.isUploading = true;
      const formData = new FormData();
      formData.append('bankStatement', this.uploadFile);
      formData.append('bankUsername', this.selectedBank);

      try {
        const response = await http.post('/orders/upload-bank-statement-by-branch', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          this.baseToast = { type: 'success', message: `Uploaded: ${response.data.report.summary.totalVerified} verified` };
          this.showUploadModal = false;
          this.selectedBank = '';
          this.uploadFile = null;
          this.getPayInList();
        } else {
          this.baseToast = { type: 'error', message: response.data.message || 'Upload failed' };
        }
      } catch (error) {
        this.baseToast = { type: 'error', message: error.response?.data?.message || 'Upload failed' };
      } finally {
        this.isUploading = false;
      }
    },
    setPage(page) {
      this.page = page;
    },
    async getUsersWithRole() {
      try {
        const response = await http.get("/users/user");
        if (response.status == 200) {
          this.usersWithRole = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    updateStatusAndTransaction(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateStatusAndTransaction", data);
      }
    },
    updateAmountAndStatus(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateAmountAndStatus", data);
      }
    },
    updateCustomerUpiID(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUpiID", data);
      }
    },
    updateCustomerUtr(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUtr", data);
      }
    },
    updateCustomerScreenshot(data) {
      if (!this.showSearch) {
        this.$store.commit("payin/updateCustomerUtrAndAttachment", data);
      }
    },
    autoOrderApproved(data) {
      this.$store.commit("payin/setPaymentStatusAndUtr", data);
    },
    addOrderToPayInList(data) {
      if (!this.showSearch && this.page == 1 && data.type == "payin") {
        this.$store.commit("payin/addOrderToPayInList", data);
      }
    },
    async getPayInList() {
      const params = {
        type: "payin",
        searchKeyWord: this.searchKeyWord,
        amount: this.amount,
        paymentStatus: this.paymentStatus,
        validatorUsername: this.validatorUsername,
        startDate: this.startDate,
        endDate: this.endDate,
        first: this.first,
        page: this.page,
      };
      this.isLoading = true;
      try {
        await this.$store.dispatch("payin/getPayInList", params);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    async exportData() {
      try {
        const XLSX = await import('xlsx');
        const list = this.$store.getters['payin/payInList'] || [];
        const data = list.map(item => ({
          'Branch User Name': item.branch_username || item.validatorUsername || '',
          'Receipt ID': item.receiptId || '',
          'Client Name': item.clientName || '',
          'Type': item.type || '',
          'Customer Name': item.customerName || '',
          'Customer UTR': item.customerUtr || '',
          'Amount': item.amount || '',
          'Payment Status': item.paymentStatus || '',
          'Payment Method': item.paymentMethod || '',
          'Validator Username': item.validatorUsername || '',
          'Transaction ID': item.transactionID || '',
          'Approved By Username': item.approvedByUsername || ''
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'PayIn');
        XLSX.writeFile(wb, `payin-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
        this.error = 'Export failed: ' + error.message;
      }
    },
    handleError() {
      this.error = null;
    },
    searchBtnClicked() {
      this.showSearch = true;
    },
    cancelBtnClicked() {
      this.showSearch = false;
      this.searchKeyWord = "";
      this.amount = null;
      this.paymentStatus = "";
      this.validatorUsername = "";
      this.startDate = "";
      this.endDate = "";
    },
  },
  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
};
</script>

<style scoped>
.verify-statement-link {
  display: inline-block;
  background: #4CAF50;
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 13px;
}

.verify-statement-link:hover {
  background: #45a049;
}
</style>
